import React, { useEffect, useMemo, useState } from 'react';
import { Clock, Plus, X } from 'lucide-react';
import { bookingApi } from '../api/bookingApi';
import { HOUR_STEP, formatHours, isValidBookingHours } from '../utils/duration';

const inputClass = 'box-border w-full rounded-2xl border-0 bg-slate-100/80 px-4 py-3.5 text-[15px] text-slate-950 outline-none ring-1 ring-inset ring-slate-200 transition-all duration-200 placeholder:text-slate-400 hover:bg-white hover:ring-slate-300 focus:bg-white focus:ring-2 focus:ring-green-500';

// Only the hours are charged. Ticking sub services simply tells the Gigger what
// the extra time should go on — no sub service has a time limit of its own.
const RequestMoreHoursModal = ({ booking, tasks = [], canAddTasks, giggerName, clientRate = 0, onClose, onChanged }) => {
  // Every service this Gigger offers, so the client can pick up a second
  // service mid-job. Services already on the booking come first.
  const services = useMemo(() => {
    const available = Array.isArray(booking?.availableServices) ? booking.availableServices : [];
    const bookedIds = new Set([
      String(booking?.service?._id || booking?.service || ''),
      ...(Array.isArray(booking?.serviceItems) ? booking.serviceItems : [])
        .map((item) => String(item.service?._id || item.service || '')),
    ].filter(Boolean));

    const list = available
      .filter((item) => item && item._id)
      .map((item) => ({ ...item, _id: String(item._id), booked: bookedIds.has(String(item._id)) }));

    if (list.length > 0) {
      return [...list].sort((a, b) => Number(b.booked) - Number(a.booked));
    }
    // Fallback for a booking whose Gigger service list did not come through.
    return [{
      _id: String(booking?.service?._id || booking?.service || ''),
      name: booking?.service?.name || 'Service',
      booked: true,
    }].filter((item) => item._id);
  }, [booking]);

  // Default to the service this booking was made for.
  const primaryServiceId = String(booking?.service?._id || booking?.service || '');
  // One entry per ticked service: { hours, taskNames }. The client can request
  // extra time on several services at once, each at that service's own rate.
  const [picked, setPicked] = useState(() => (
    services.some((item) => item._id === primaryServiceId)
      ? { [primaryServiceId]: { hours: 1, taskNames: [] } }
      : (services[0] ? { [services[0]._id]: { hours: 1, taskNames: [] } } : {})
  ));
  const [details, setDetails] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const hasPendingTask = tasks.some((task) => task.status === 'pending');
  const hasAcceptedUnpaid = tasks.some((task) => task.status === 'accepted' && !task.paidAt) && booking?.payment?.balanceStatus !== 'paid';
  const hasOpenRequest = hasPendingTask || hasAcceptedUnpaid;

  const clientRateFor = (item) => (Number(item?.hourlyRate) > 0 ? Number(item.hourlyRate) * 1.2 : clientRate);
  const pickedServices = services.filter((item) => picked[item._id]);
  const totalHours = pickedServices.reduce((sum, item) => sum + (Number(picked[item._id].hours) || 0), 0);
  const previewCost = pickedServices.reduce(
    (sum, item) => sum + ((Number(picked[item._id].hours) || 0) * clientRateFor(item)),
    0
  );
  const allHoursValid = pickedServices.length > 0
    && pickedServices.every((item) => isValidBookingHours(picked[item._id].hours));

  useEffect(() => {
    const onKey = (event) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const toggleService = (item) => {
    setPicked((current) => {
      const next = { ...current };
      if (next[item._id]) delete next[item._id];
      else next[item._id] = { hours: 1, taskNames: [] };
      return next;
    });
    setError('');
  };

  const setServiceHours = (item, value) => {
    setPicked((current) => ({
      ...current,
      [item._id]: { ...current[item._id], hours: value === '' ? '' : Number(value) },
    }));
    setError('');
  };

  const toggleTask = (item, task) => {
    setPicked((current) => {
      const entry = current[item._id];
      if (!entry) return current;
      const taskNames = entry.taskNames.includes(task)
        ? entry.taskNames.filter((name) => name !== task)
        : [...entry.taskNames, task];
      return { ...current, [item._id]: { ...entry, taskNames } };
    });
  };

  const send = async () => {
    if (busy || hasOpenRequest) return;
    if (pickedServices.length === 0) {
      setError('Choose at least one service.');
      return;
    }
    const invalid = pickedServices.find((item) => !isValidBookingHours(picked[item._id].hours));
    if (invalid) {
      setError(`${invalid.name}: enter the extra time in half-hour steps, starting at ${formatHours(HOUR_STEP)}.`);
      return;
    }
    setBusy(true);
    setError('');
    try {
      await bookingApi.requestExtraHours(booking.trackingId, {
        services: pickedServices.map((item) => ({
          serviceId: item._id,
          hours: Number(picked[item._id].hours),
          taskNames: picked[item._id].taskNames,
        })),
        details: details.trim(),
      });
      setPicked({});
      setDetails('');
      await onChanged?.();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not send the request.');
    } finally {
      setBusy(false);
    }
  };

  const RequestRow = ({ task }) => {
    const badge = task.paidAt ? 'Paid' : task.status === 'accepted' ? 'Accepted' : task.status === 'declined' ? 'Declined' : `Awaiting ${giggerName}`;
    const badgeClass = task.status === 'accepted' ? 'bg-green-50 text-green-700' : task.status === 'declined' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700';
    const rate = Number(task.hourlyRate) > 0 ? Number(task.hourlyRate) * 1.2 : clientRate;
    const cost = (Number(task.hours) || 0) * rate;
    const taskNames = Array.isArray(task.taskNames) && task.taskNames.length
      ? task.taskNames
      : (task.taskName ? [task.taskName] : []);
    return (
      <div className="min-w-0 rounded-2xl border border-slate-200 bg-white p-4">
        <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
          <div className="min-w-0">
            <div className="break-words text-sm font-bold text-slate-950">{formatHours(task.hours)} more</div>
            {task.serviceName && <div className="mt-1 text-[11px] font-bold uppercase tracking-wide text-green-700">{task.serviceName}</div>}
            {taskNames.length > 0 && <div className="mt-1 break-words text-xs leading-5 text-slate-600">For: {taskNames.join(', ')}</div>}
            {task.details && <div className="mt-1 break-words text-xs leading-5 text-slate-500">{task.details}</div>}
          </div>
          <span className={`w-fit shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold ${badgeClass}`}>{badge}</span>
        </div>
        {task.status === 'accepted' && (
          <div className="mt-2 text-right text-xs font-black text-green-700">+${cost.toFixed(2)}</div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center overflow-y-auto bg-slate-950/60 p-3 backdrop-blur-sm sm:p-4" onClick={onClose}>
      <div className="my-auto flex max-h-[calc(100dvh-1.5rem)] w-full max-w-xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl sm:max-h-[calc(100dvh-2rem)] sm:rounded-3xl" onClick={(event) => event.stopPropagation()}>
        <div className="flex shrink-0 items-center justify-between gap-4 border-b border-slate-100 px-4 py-4 sm:px-6 sm:py-5">
          <div className="flex min-w-0 items-center gap-2">
            <Clock size={19} className="shrink-0 text-green-700" />
            <h2 className="m-0 min-w-0 break-words text-lg font-black text-slate-950">Request more hours</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600"><X size={19} /></button>
        </div>

        <div className="min-h-0 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5">
          <p className="m-0 break-words text-xs leading-5 text-slate-500">Ask {giggerName} for extra time on this job. Tick any services they offer — including ones not on this booking — and set the hours for each. You are charged for the hours only, at each service's own rate; ticking sub services just tells them what the time is for.</p>
          {tasks.length === 0 ? (
            <div className="mt-4 rounded-2xl bg-slate-50 p-5 text-center text-sm text-slate-500">No extra hours requested yet.{canAddTasks ? ' Request some below.' : ''}</div>
          ) : (
            <div className="mt-4 space-y-3">{tasks.map((task) => <RequestRow task={task} key={task._id} />)}</div>
          )}
        </div>

        {canAddTasks && !hasOpenRequest && (
          <div className="shrink-0 border-t border-slate-100 px-4 py-4 sm:px-6 sm:py-5">
            {error && <div className="mb-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">{error}</div>}

            <div className="mb-2 text-xs font-bold text-slate-700">Which services? <span className="font-medium text-slate-400">(tick one or more)</span></div>
            <div className="grid gap-2 sm:grid-cols-2">
              {services.map((item) => {
                const checked = Boolean(picked[item._id]);
                return (
                  <label key={item._id} className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition ${checked ? 'border-green-300 bg-green-50' : 'border-slate-200 bg-white hover:border-green-200'}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggleService(item)} className="mt-0.5 h-4 w-4 shrink-0 accent-green-700" />
                    <span className="min-w-0 flex-1">
                      <span className="block break-words text-sm font-bold text-slate-800">{item.name}</span>
                      <span className="mt-0.5 block text-[11px] font-semibold text-slate-500">
                        {Number(item.hourlyRate) > 0 ? `$${clientRateFor(item).toFixed(2)}/hr` : 'Rate on request'}
                        {item.booked ? '' : ' · new service'}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>

            {pickedServices.length > 0 && (
              <div className="mt-3 space-y-3">
                {pickedServices.map((item) => {
                  const entry = picked[item._id];
                  const checklist = Array.isArray(item.checklist) ? item.checklist : [];
                  return (
                    <div key={item._id} className="rounded-2xl border border-slate-200 bg-white p-3.5">
                      <div className="flex flex-wrap items-end justify-between gap-3">
                        <div className="min-w-0">
                          <div className="break-words text-sm font-bold text-slate-900">{item.name}</div>
                          {!item.booked && (
                            <div className="mt-0.5 text-[11px] font-semibold text-green-700">Not on this booking yet — {giggerName} will be asked to add it.</div>
                          )}
                        </div>
                        <label className="w-[130px] shrink-0">
                          <span className="mb-1.5 block text-[11px] font-bold text-slate-700">Extra hours</span>
                          <input
                            type="number"
                            min={HOUR_STEP}
                            step={HOUR_STEP}
                            inputMode="decimal"
                            value={entry.hours}
                            onChange={(event) => setServiceHours(item, event.target.value)}
                            aria-label={`${item.name} extra hours`}
                            className={`${inputClass} !py-2.5 text-center`}
                          />
                        </label>
                      </div>
                      {checklist.length > 0 && (
                        <div className="mt-3">
                          <div className="mb-2 text-[11px] font-bold text-slate-600">Sub services <span className="font-medium text-slate-400">(optional)</span></div>
                          <div className="flex flex-wrap gap-2">
                            {checklist.map((task) => {
                              const taskChecked = entry.taskNames.includes(task);
                              return (
                                <button
                                  type="button"
                                  key={task}
                                  onClick={() => toggleTask(item, task)}
                                  aria-pressed={taskChecked}
                                  className={`cursor-pointer break-words rounded-full border px-3 py-1.5 text-[11px] font-bold transition-colors ${taskChecked ? 'border-green-600 bg-green-50 text-green-800' : 'border-slate-200 bg-white text-slate-600'}`}
                                >
                                  {task}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <label className="mt-3 block">
              <span className="mb-2 block text-xs font-bold text-slate-700">More information <span className="font-medium text-slate-400">(optional)</span></span>
              <input
                type="text"
                maxLength={500}
                value={details}
                onChange={(event) => setDetails(event.target.value)}
                placeholder="Anything the Gigger should know"
                className={inputClass}
              />
            </label>

            {previewCost > 0 && (
              <div className="mt-3 rounded-xl bg-green-50 px-3.5 py-2.5 text-xs font-bold text-green-800">{formatHours(totalHours)} extra across {pickedServices.length} service{pickedServices.length === 1 ? '' : 's'} · approximately ${previewCost.toFixed(2)}</div>
            )}

            <button disabled={busy || !allHoursValid} onClick={send} className="mt-4 inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl border-0 bg-green-700 px-5 py-3 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-40">{busy ? 'Sending...' : <><Plus size={16} /> Request {allHoursValid ? formatHours(totalHours) : 'more hours'}</>}</button>
          </div>
        )}
        {canAddTasks && hasOpenRequest && (
          <div className="shrink-0 border-t border-amber-200 bg-amber-50 px-4 py-4 text-xs leading-5 text-amber-900 sm:px-6">
            {hasPendingTask ? `Waiting for ${giggerName} to respond to your request for more hours.` : 'Accepted extra hours are waiting for your advance payment.'}
          </div>
        )}
      </div>
    </div>
  );
};

export default RequestMoreHoursModal;
